import { Box, ButtonBase, IconButton, styled, Typography } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { Badge } from '../badge/Badge';
import Chip from '../chip/Chip';
import { Stack } from '../stack/Stack';
import { SegmentProps, SegmentedControlProps } from './types';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const ScrollButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.dividerDark}`,
    padding: 4,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
    },
    '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
        color: theme.palette.text.primary,
    },
}));

const SegmentContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    backgroundColor: theme.palette.grey[100],
    padding: '2px 4px',
    borderRadius: 8,
    gap: theme.spacing(0.5),
    position: 'relative',
    overflowX: 'hidden',
    margin: 0,
    minHeight: 44,
}));

const ScrollContainer = styled(Box)({
    display: 'flex',
    transition: 'transform 0.4s cubic-bezier(0.34, 1.12, 0.64, 1)',
    gap: 'inherit',
    position: 'relative',
});

const SelectionIndicator = styled(Box)(({ theme }) => ({
    position: 'absolute',
    height: 'calc(100% - 4px)',
    top: 2,
    backgroundColor: theme.palette.common.white,
    borderRadius: 6,
    border: `1px solid ${theme.palette.dividerDark}`,
    transition: 'all 0.4s cubic-bezier(0.34, 1.12, 0.64, 1)',
    zIndex: 1,
    boxSizing: 'border-box',
    willChange: 'transform, width',
    boxShadow: `0px 2px 4px ${theme.palette.neutral.dark}14`,
}));

const SegmentButton = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'fullWidth',
})<{ isSelected?: boolean; fullWidth?: boolean }>(({ theme, isSelected, fullWidth }) => ({
    borderRadius: 6,
    padding: theme.spacing(1, 2),
    transition: 'color 0.4s cubic-bezier(0.34, 1.12, 0.64, 1)',
    color: theme.palette.text.primary,
    position: 'relative',
    zIndex: 2,
    '&:hover': {
        backgroundColor: 'transparent',
    },
    flex: fullWidth ? 1 : 'initial',
    minWidth: 80,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
        color: theme.palette.text.primary,
        fontSize: '1.25rem',
        flexShrink: 0,
        width: 20,
        height: 20,
    },
    '& .MuiTypography-root': {
        fontWeight: isSelected ? 600 : 400,
        transition: 'font-weight 0.2s ease',
        display: 'block',
        whiteSpace: 'nowrap',
        lineHeight: 1.5,
        minWidth: 'fit-content',
    },
    '& .MuiChip-root': {
        flexShrink: 0,
    },
    '& .Badge-root': {
        flexShrink: 0,
    },
}));

const SegmentLabel = ({ label, startIcon, endIcon, chip, badge, isSelected }: SegmentProps & { isSelected?: boolean }) => {
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
                height: '100%',
                width: '100%',
                justifyContent: 'center',
            }}
        >
            {startIcon && (
                <Box component="span" sx={{ display: 'flex' }}>
                    {React.createElement(startIcon)}
                </Box>
            )}
            <Typography variant="body2" component="span">
                {label}
            </Typography>
            {endIcon && (
                <Box component="span" sx={{ display: 'flex' }}>
                    {React.createElement(endIcon)}
                </Box>
            )}
            {chip && (
                <Box component="span" sx={{ display: 'flex' }}>
                    <Chip label={chip.label} color={chip.color} />
                </Box>
            )}
            {badge && (
                <Box component="span" sx={{ display: 'flex' }}>
                    <Badge color={badge.color} badgeContent={badge.value} />
                </Box>
            )}
        </Stack>
    );
};

export const SegmentedControl = ({ segments, selectedSegment, onChange, size = 'medium', fullWidth = false }: SegmentedControlProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [indicatorStyle, setIndicatorStyle] = useState({
        width: 0,
        transform: 'translateX(0)',
    });

    const updateIndicatorPosition = () => {
        const selectedButton = segmentRefs.current[selectedSegment];
        if (selectedButton && scrollContainerRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const buttonRect = selectedButton.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();

            const relativeLeft = buttonRect.left - containerRect.left;

            requestAnimationFrame(() => {
                setIndicatorStyle({
                    width: buttonRect.width,
                    transform: `translateX(${relativeLeft}px)`,
                });
            });
        }
    };

    useEffect(() => {
        const handleResize = () => {
            requestAnimationFrame(() => {
                updateScrollButtons();
                updateIndicatorPosition();
            });
        };

        updateScrollButtons();
        updateIndicatorPosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [segments]);

    const updateScrollButtons = () => {
        if (!containerRef.current || !scrollContainerRef.current) return;

        const container = containerRef.current;
        const scrollContainer = scrollContainerRef.current;

        setShowLeftButton(scrollPosition > 0);
        setShowRightButton(scrollPosition < scrollContainer.scrollWidth - container.clientWidth);
    };

    const scroll = (direction: 'left' | 'right') => {
        if (!containerRef.current || !scrollContainerRef.current) return;

        const container = containerRef.current;
        const scrollAmount = container.clientWidth * 0.8;
        const newPosition =
            direction === 'left'
                ? Math.max(0, scrollPosition - scrollAmount)
                : Math.min(scrollContainerRef.current.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);

        setScrollPosition(newPosition);
        updateScrollButtons();
    };

    return (
        <SegmentContainer ref={containerRef}>
            {showLeftButton && (
                <ScrollButton onClick={() => scroll('left')} sx={{ left: 4 }} size="small">
                    <KeyboardArrowLeftIcon />
                </ScrollButton>
            )}

            <ScrollContainer ref={scrollContainerRef} sx={{ transform: `translateX(-${scrollPosition}px)` }}>
                <SelectionIndicator sx={indicatorStyle} />
                {segments?.map((segment, index) => (
                    <SegmentButton
                        key={segment.key || index}
                        onClick={() => onChange(index)}
                        isSelected={selectedSegment === index}
                        disabled={segment.disabled}
                        fullWidth={fullWidth}
                        sx={{ py: size === 'small' ? 0.5 : 1 }}
                        ref={(el) => (segmentRefs.current[index] = el)}
                        disableRipple
                    >
                        <SegmentLabel {...segment} isSelected={selectedSegment === index} />
                    </SegmentButton>
                ))}
            </ScrollContainer>

            {showRightButton && (
                <ScrollButton onClick={() => scroll('right')} sx={{ right: 4 }} size="small">
                    <KeyboardArrowRightIcon />
                </ScrollButton>
            )}
        </SegmentContainer>
    );
};
